import { useCallback, useEffect, useRef, useState } from 'react';
import {
  BLOCK_COLORS,
  BLOCK_TYPES,
  TEMPLATES,
  getBlockType,
  getTemplate,
  renderPageContent,
  type SitePage,
  type TemplateField,
} from '@visionqc/shared';
import { api } from '../api/client';
import { useIsAdmin } from '../api/auth';
import { fmtTime } from '../lib/format';

const SITE_URL = 'https://www.thevisionqc.com';

/**
 * Website CMS — edit the public marketing site without touching code.
 *
 * A page picks a template, and the template declares its fields; this editor is
 * generated from that declaration, so adding a template in shared code adds it
 * here automatically.
 */
export function CmsView() {
  const isAdmin = useIsAdmin();
  const [pages, setPages] = useState<SitePage[]>([]);
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const load = useCallback(async () => {
    const p = await api.listSitePages().catch(() => []);
    setPages(p);
    setSelectedId((cur) => (cur && p.some((x) => x.id === cur) ? cur : (p[0]?.id ?? null)));
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const selected = pages.find((p) => p.id === selectedId) ?? null;

  if (!isAdmin) {
    return (
      <div>
        <h1 className="view-title">Website</h1>
        <div className="panel muted">Editing the website requires the ADMIN role.</div>
      </div>
    );
  }

  return (
    <div>
      <div className="row" style={{ justifyContent: 'space-between' }}>
        <h1 className="view-title">Website</h1>
        <div className="row">
          <a className="btn sm" href={SITE_URL} target="_blank" rel="noreferrer">
            Open site ↗
          </a>
          <button className="btn primary" onClick={() => setCreating((c) => !c)}>
            {creating ? '✕ Cancel' : '+ New page'}
          </button>
        </div>
      </div>

      {error && <div className="panel" style={{ color: 'var(--bad)', marginBottom: 16 }}>{error}</div>}
      {notice && <div className="panel" style={{ color: 'var(--good)', marginBottom: 16 }}>{notice}</div>}

      {creating && (
        <NewPage
          onCreated={async (p) => {
            setCreating(false);
            await load();
            setSelectedId(p.id);
            setNotice(`Created /${p.slug}. It is a draft until you publish it.`);
          }}
          onError={setError}
        />
      )}

      <div className="grid" style={{ gridTemplateColumns: '280px minmax(0, 1fr)' }}>
        <div className="panel" style={{ padding: 12, alignSelf: 'start' }}>
          <div
            className="muted"
            style={{ fontSize: 12, textTransform: 'uppercase', letterSpacing: 1, marginBottom: 8 }}
          >
            {pages.length} page{pages.length === 1 ? '' : 's'}
          </div>
          <div className="grid" style={{ gap: 6 }}>
            {pages.map((p) => (
              <button
                key={p.id}
                className={`chip ${selectedId === p.id ? 'selected' : ''}`}
                style={{ textAlign: 'left', display: 'block', width: '100%' }}
                onClick={() => setSelectedId(p.id)}
              >
                <div style={{ fontWeight: 700 }}>{p.title}</div>
                <div className="muted" style={{ fontSize: 11, marginTop: 3 }}>
                  /{p.slug} · {p.template}
                  {p.published ? '' : ' · draft'}
                </div>
              </button>
            ))}
            {pages.length === 0 && (
              <span className="muted" style={{ fontSize: 13 }}>
                No pages yet. The built-in pages (home, how it works, contact, legal) are part of the
                site itself — add pages here to extend it.
              </span>
            )}
          </div>
        </div>

        {selected ? (
          <PageEditor
            key={selected.id}
            page={selected}
            onSaved={async (msg) => {
              await load();
              setNotice(msg);
              setError(null);
            }}
            onDeleted={async () => {
              await load();
              setSelectedId(null);
              setNotice('Page deleted.');
            }}
            onError={setError}
          />
        ) : (
          <div className="panel muted">Select a page, or create one.</div>
        )}
      </div>
    </div>
  );
}

function NewPage({
  onCreated,
  onError,
}: {
  onCreated: (p: SitePage) => void;
  onError: (m: string) => void;
}) {
  const [template, setTemplate] = useState(TEMPLATES[0]!.id);
  const [title, setTitle] = useState('');
  const [slug, setSlug] = useState('');

  // Suggest an address from the title, but let it be overridden.
  const suggested = title
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');

  const create = async () => {
    try {
      const page = await api.createSitePage({
        slug: (slug || suggested).trim(),
        title: title.trim(),
        description: '',
        template,
        content: { title: title.trim() },
        published: false,
        showInNav: false,
        sortOrder: 0,
      });
      onCreated(page);
    } catch (e) {
      onError((e as Error).message);
    }
  };

  return (
    <div className="panel" style={{ marginBottom: 20 }}>
      <strong>New page</strong>
      <p className="muted" style={{ fontSize: 12, marginTop: 4 }}>
        Pick a template — it decides which fields the page has. You can change it later.
      </p>

      <div
        className="grid"
        style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 12, margin: '14px 0' }}
      >
        {TEMPLATES.map((t) => (
          <button
            key={t.id}
            className={`chip ${template === t.id ? 'selected' : ''}`}
            style={{ display: 'block', textAlign: 'left', width: '100%' }}
            onClick={() => setTemplate(t.id)}
          >
            <div style={{ fontWeight: 700 }}>{t.name}</div>
            <div className="muted" style={{ fontSize: 11, marginTop: 3 }}>
              {t.description}
            </div>
          </button>
        ))}
      </div>

      <div className="row" style={{ alignItems: 'flex-end' }}>
        <div style={{ flex: 1 }}>
          <label>Page title</label>
          <input
            autoFocus
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="e.g. About us"
            style={{ width: '100%' }}
          />
        </div>
        <div style={{ flex: 1 }}>
          <label>Address</label>
          <input
            value={slug || suggested}
            onChange={(e) => setSlug(e.target.value)}
            placeholder="about-us"
            style={{ width: '100%' }}
          />
        </div>
        <button className="btn primary" onClick={create} disabled={!title.trim()}>
          Create
        </button>
      </div>
    </div>
  );
}

function PageEditor({
  page,
  onSaved,
  onDeleted,
  onError,
}: {
  page: SitePage;
  onSaved: (msg: string) => void;
  onDeleted: () => void;
  onError: (m: string) => void;
}) {
  const [content, setContent] = useState<Record<string, unknown>>(page.content ?? {});
  const [title, setTitle] = useState(page.title);
  const [description, setDescription] = useState(page.description);
  const [navLabel, setNavLabel] = useState(page.navLabel ?? '');
  const [showInNav, setShowInNav] = useState(page.showInNav);
  const [sortOrder, setSortOrder] = useState(page.sortOrder);
  const [saving, setSaving] = useState(false);
  const [showPreview, setShowPreview] = useState(true);
  const template = getTemplate(page.template);

  // Edits live in local state until saved, so say so plainly — otherwise a
  // change that hasn't been sent looks like a change that didn't work.
  const dirty =
    title !== page.title ||
    description !== page.description ||
    navLabel !== (page.navLabel ?? '') ||
    showInNav !== page.showInNav ||
    sortOrder !== page.sortOrder ||
    JSON.stringify(content) !== JSON.stringify(page.content ?? {});

  useEffect(() => {
    if (!dirty) return;
    const warn = (e: BeforeUnloadEvent) => e.preventDefault();
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [dirty]);

  const save = async (extra: Record<string, unknown> = {}, msg = 'Saved.') => {
    setSaving(true);
    try {
      await api.updateSitePage(page.id, {
        title,
        description,
        content,
        navLabel: navLabel || null,
        showInNav,
        sortOrder,
        ...extra,
      });
      onSaved(msg);
    } catch (e) {
      onError((e as Error).message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="grid" style={{ gap: 16, alignContent: 'start' }}>
      <div className="panel">
        <div className="row" style={{ justifyContent: 'space-between' }}>
          <div>
            <strong style={{ fontSize: 18 }}>{page.title}</strong>
            <div className="muted" style={{ fontSize: 12, marginTop: 4 }}>
              <a href={`${SITE_URL}/${page.slug}`} target="_blank" rel="noreferrer">
                /{page.slug} ↗
              </a>{' '}
              · {template?.name ?? page.template} · edited by {page.updatedBy ?? '—'}{' '}
              {fmtTime(page.updatedAt)}
            </div>
          </div>
          <div className="row">
            {dirty && <span className="badge unsure">UNSAVED CHANGES</span>}
            <span className={`badge ${page.published ? 'good' : 'unsure'}`}>
              {page.published ? 'PUBLISHED' : 'DRAFT'}
            </span>
            <button
              className={page.published ? 'btn sm' : 'btn primary sm'}
              disabled={saving}
              onClick={() =>
                save(
                  { published: !page.published },
                  page.published ? 'Unpublished — the page is no longer public.' : 'Published.',
                )
              }
            >
              {page.published ? 'Unpublish' : 'Publish'}
            </button>
          </div>
        </div>
      </div>

      <div className="panel grid" style={{ gap: 14 }}>
        <div>
          <label>Page title</label>
          <input value={title} onChange={(e) => setTitle(e.target.value)} style={{ width: '100%' }} />
        </div>
        <div>
          <label>Search-engine description</label>
          <input
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="One sentence shown in search results"
            style={{ width: '100%' }}
          />
        </div>
        <div className="row" style={{ alignItems: 'flex-end' }}>
          <label className="row" style={{ gap: 8, margin: 0, paddingBottom: 10 }}>
            <input type="checkbox" checked={showInNav} onChange={(e) => setShowInNav(e.target.checked)} />
            Show in the site menu
          </label>
          {showInNav && (
            <>
              <div>
                <label>Menu label</label>
                <input
                  value={navLabel}
                  onChange={(e) => setNavLabel(e.target.value)}
                  placeholder={page.title}
                  style={{ maxWidth: 220 }}
                />
              </div>
              <div>
                <label>Menu position</label>
                <input
                  type="number"
                  value={sortOrder}
                  onChange={(e) => setSortOrder(Number(e.target.value))}
                  style={{ width: 110 }}
                  title="Lower numbers appear first in the menu"
                />
              </div>
            </>
          )}
        </div>
      </div>

      <div className="cms-split">
        <div className="grid" style={{ gap: 16, alignContent: 'start', minWidth: 0 }}>
          {template?.id === 'blocks' ? (
            <>
            <FieldEditor
              field={{ key: 'title', label: 'Page heading', type: 'text' }}
              value={content.title}
              onChange={(v) => setContent((c) => ({ ...c, title: v }))}
              onError={onError}
            />
            <FieldEditor
              field={{ key: 'lead', label: 'Intro paragraph', type: 'textarea' }}
              value={content.lead}
              onChange={(v) => setContent((c) => ({ ...c, lead: v }))}
              onError={onError}
            />
            <BlockEditor
              blocks={Array.isArray(content.blocks) ? (content.blocks as Record<string, unknown>[]) : []}
              onChange={(blocks) => setContent((c) => ({ ...c, blocks }))}
              onError={onError}
            />
            </>
          ) : (
            <>
              {template?.fields.map((f) => (
                <FieldEditor
                  key={f.key}
                  field={f}
                  value={content[f.key]}
                  onChange={(v) => setContent((c) => ({ ...c, [f.key]: v }))}
                  onError={onError}
                />
              ))}
              {/* A fixed template can't grow on its own, so a page like the
                  landing page had a hard ceiling: whatever the template didn't
                  model simply couldn't be added. Extra blocks render after the
                  template's own sections. */}
              <BlockEditor
                blocks={Array.isArray(content.blocks) ? (content.blocks as Record<string, unknown>[]) : []}
                onChange={(blocks) => setContent((c) => ({ ...c, blocks }))}
                onError={onError}
              />
            </>
          )}
        </div>
        {showPreview ? (
          <LivePreview page={{ ...page, title, content }} onHide={() => setShowPreview(false)} />
        ) : (
          <button className="btn sm" style={{ alignSelf: 'start' }} onClick={() => setShowPreview(true)}>
            Show live preview
          </button>
        )}
      </div>

      <PageHistory page={page} onRestored={onSaved} onError={onError} />

      <div className="panel row" style={{ justifyContent: 'space-between' }}>
        <button
          className="btn sm"
          onClick={async () => {
            if (!window.confirm(`Delete "${page.title}"? This cannot be undone.`)) return;
            await api.deleteSitePage(page.id).catch((e) => onError((e as Error).message));
            onDeleted();
          }}
        >
          Delete page
        </button>
        <button
          className="btn primary"
          disabled={saving || !dirty}
          onClick={() => save()}
          title={dirty ? 'Publish your edits to the site' : 'Nothing to save'}
        >
          {saving ? 'Saving…' : dirty ? 'Save changes' : 'Saved'}
        </button>
      </div>
    </div>
  );
}

/**
 * Live preview.
 *
 * Rendered with renderPageContent — the very same function the website uses —
 * inside an iframe carrying the marketing site's own stylesheet. A preview built
 * from a second copy of the markup would drift, and a preview that lies is worse
 * than none.
 */
function LivePreview({ page, onHide }: { page: SitePage; onHide?: () => void }) {
  const [device, setDevice] = useState<'desktop' | 'mobile'>('desktop');
  const [zoom, setZoom] = useState<number | 'fit'>('fit');
  const [contentHeight, setContentHeight] = useState(1200);
  const frameRef = useRef<HTMLIFrameElement>(null);
  const boxRef = useRef<HTMLDivElement>(null);
  const [boxWidth, setBoxWidth] = useState(600);

  // The preview renders at a real browser width and is then scaled down, so the
  // layout you see is the layout the site produces — not a narrow reflow.
  const logicalWidth = device === 'mobile' ? 390 : 1280;

  useEffect(() => {
    const el = boxRef.current;
    if (!el) return;
    const ro = new ResizeObserver(() => setBoxWidth(el.clientWidth));
    ro.observe(el);
    setBoxWidth(el.clientWidth);
    return () => ro.disconnect();
  }, []);

  /*
   * The preview runs on the admin host but the page's assets live on the website
   * host, so site-relative paths would 404 here and every image would look
   * broken. Rewrite them onto routes this host does serve:
   *   /img/...        -> the website, through the preview proxy
   *   /site-media/... -> the backend's public media route
   */
  const body = renderPageContent(page)
    .replace(/src="\/site-media\//g, 'src="/api/site/media/')
    .replace(/src="\/(img|fonts)\//g, 'src="/site-preview/$1/')
    .replace(
      '{{form}}',
      '<div class="callout c-violet">The enquiry form appears here on the live site.</div>',
    );

  const html = `<!doctype html><html><head><meta charset="utf-8" />
    <base target="_blank" />
    <link rel="stylesheet" href="/site-preview/style.css" />
    <style>
      html, body { overflow: hidden; }
      body { padding: 0; }
      .img-placeholder {
        display:flex;align-items:center;justify-content:center;
        aspect-ratio:16/10;border:1px dashed var(--border);border-radius:12px;
        color:var(--dim);font-size:13px;
      }
    </style></head>
    <body><main>${body}</main></body></html>`;

  /** Grow the frame to the full document height so nothing is cut off. */
  const measure = () => {
    const doc = frameRef.current?.contentDocument;
    if (doc?.body) setContentHeight(Math.max(600, doc.body.scrollHeight + 40));
  };
  useEffect(() => {
    const t = setTimeout(measure, 60); // after the stylesheet applies
    return () => clearTimeout(t);
  }, [html]);

  const scale = zoom === 'fit' ? Math.min(1, boxWidth / logicalWidth) : zoom;

  return (
    <div
      className="panel"
      style={{
        position: 'sticky',
        top: 20,
        alignSelf: 'start',
        padding: 12,
        // The iframe is rendered at full browser width and only *visually*
        // scaled — transform doesn't shrink the layout box, so without these
        // the panel is sized by 1280px of content and covers the column beside it.
        minWidth: 0,
        overflow: 'hidden',
        zIndex: 1,
      }}
    >
      <div className="row" style={{ justifyContent: 'space-between', marginBottom: 10 }}>
        <strong style={{ fontSize: 14 }}>Live preview</strong>
        <div className="row" style={{ gap: 6 }}>
          {(['desktop', 'mobile'] as const).map((d) => (
            <button
              key={d}
              className={`btn sm ${device === d ? 'primary' : ''}`}
              onClick={() => setDevice(d)}
            >
              {d === 'desktop' ? 'Desktop' : 'Mobile'}
            </button>
          ))}
          {onHide && (
            <button className="btn sm" onClick={onHide} title="Hide the preview">
              ✕
            </button>
          )}
          <select
            value={String(zoom)}
            onChange={(e) => setZoom(e.target.value === 'fit' ? 'fit' : Number(e.target.value))}
            style={{ padding: '5px 8px', fontSize: 13 }}
          >
            <option value="fit">Fit width</option>
            <option value="1">100%</option>
            <option value="0.75">75%</option>
            <option value="0.5">50%</option>
            <option value="0.33">33%</option>
          </select>
        </div>
      </div>

      {/* Scroll container: the whole page is rendered, scaled, and scrollable. */}
      <div
        ref={boxRef}
        style={{
          height: '74vh',
          overflow: 'auto',
          maxWidth: '100%',
          border: '1px solid var(--border)',
          borderRadius: 8,
          background: '#0a0d12',
        }}
      >
        <div
          style={{
            width: logicalWidth * scale,
            height: contentHeight * scale,
            margin: '0 auto',
            overflow: 'hidden',
          }}
        >
          <iframe
            ref={frameRef}
            title="Page preview"
            srcDoc={html}
            sandbox="allow-same-origin"
            onLoad={measure}
            style={{
              width: logicalWidth,
              height: contentHeight,
              border: 0,
              display: 'block',
              transform: `scale(${scale})`,
              transformOrigin: 'top left',
            }}
          />
        </div>
      </div>
      <p className="muted" style={{ fontSize: 12, margin: '10px 0 0' }}>
        The whole page, updating as you type — scroll or zoom out to see it all. Changes go live
        once you save and publish.
      </p>
    </div>
  );
}

/**
 * Block editor — choose modules, order them, fill them in.
 *
 * Fixed templates couldn't grow: an About page had nowhere to put an image and a
 * team page had no shape at all. Here the page is a list of blocks you pick from
 * a chooser, and each block declares its own fields, so FieldEditor renders them
 * unchanged.
 */
function BlockEditor({
  blocks,
  onChange,
  onError,
}: {
  blocks: Record<string, unknown>[];
  onChange: (b: Record<string, unknown>[]) => void;
  onError: (m: string) => void;
}) {
  const [adding, setAdding] = useState(false);

  const setField = (i: number, key: string, value: unknown) =>
    onChange(blocks.map((b, k) => (k === i ? { ...b, [key]: value } : b)));

  const move = (i: number, dir: -1 | 1) => {
    const target = i + dir;
    if (target < 0 || target >= blocks.length) return;
    const next = [...blocks];
    [next[i], next[target]] = [next[target]!, next[i]!];
    onChange(next);
  };

  return (
    <div className="grid" style={{ gap: 14 }}>
      <div className="panel">
        <div className="row" style={{ justifyContent: 'space-between' }}>
          <strong>Content blocks</strong>
          <button className="btn primary sm" onClick={() => setAdding((a) => !a)}>
            {adding ? '✕ Cancel' : '+ Add block'}
          </button>
        </div>
        {adding && (
          <div className="grid" style={{ gap: 8, marginTop: 12 }}>
            {BLOCK_TYPES.map((b) => (
              <button
                key={b.id}
                className="chip"
                style={{ display: 'block', textAlign: 'left', width: '100%' }}
                onClick={() => {
                  onChange([...blocks, { type: b.id }]);
                  setAdding(false);
                }}
              >
                <div style={{ fontWeight: 700 }}>{b.name}</div>
                <div className="muted" style={{ fontSize: 11, marginTop: 3 }}>
                  {b.description}
                </div>
              </button>
            ))}
          </div>
        )}
        {blocks.length === 0 && !adding && (
          <p className="muted" style={{ fontSize: 13, marginBottom: 0 }}>
            This page is empty. Add a block to start — text, an image, your team, cards or figures.
          </p>
        )}
      </div>

      {blocks.map((block, i) => {
        const type = getBlockType(String(block.type));
        return (
          <div key={i} className="panel">
            <div className="row" style={{ justifyContent: 'space-between', marginBottom: 10 }}>
              <strong style={{ fontSize: 15 }}>
                {type?.name ?? String(block.type)}{' '}
                <span className="muted" style={{ fontWeight: 400, fontSize: 12 }}>
                  · block {i + 1}
                </span>
              </strong>
              <div className="row" style={{ gap: 6 }}>
                {/* Half-width blocks pair up with their neighbours into two
                    columns, so a run of short blocks reads as a grid instead of
                    a long stack of full-width bands. */}
                <button
                  className={`btn sm${block.width === 'half' ? ' primary' : ''}`}
                  title={
                    block.width === 'half'
                      ? 'Half width — shares a row with the next half-width block'
                      : 'Full width — spans the page on its own'
                  }
                  onClick={() => setField(i, 'width', block.width === 'half' ? 'full' : 'half')}
                >
                  {block.width === 'half' ? '◫ Half' : '▭ Full'}
                </button>
                <button className="btn sm" disabled={i === 0} onClick={() => move(i, -1)} title="Move up">
                  ↑
                </button>
                <button
                  className="btn sm"
                  disabled={i === blocks.length - 1}
                  onClick={() => move(i, 1)}
                  title="Move down"
                >
                  ↓
                </button>
                <button
                  className="btn sm"
                  onClick={() => {
                    if (window.confirm(`Remove this ${type?.name ?? 'block'}?`)) {
                      onChange(blocks.filter((_, k) => k !== i));
                    }
                  }}
                >
                  ✕
                </button>
              </div>
            </div>

            {/* Presentation, kept apart from the block's own content fields:
                whether it sits in a card, and what colour its edge is. */}
            <div
              className="row"
              style={{
                gap: 14,
                marginBottom: 12,
                paddingBottom: 12,
                borderBottom: '1px solid var(--border)',
                flexWrap: 'wrap',
              }}
            >
              <label className="row" style={{ gap: 7, fontSize: 13, cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={block.card === true}
                  onChange={(e) => setField(i, 'card', e.target.checked)}
                />
                Show as card
              </label>

              {/* Colour is not a card-only setting: image and image-beside-text
                  blocks carry an accent too, and left to the default it changed
                  whenever the block moved. */}
              <div className="row" style={{ gap: 7, alignItems: 'center' }}>
                <span className="muted" style={{ fontSize: 12 }}>
                  {block.card === true ? 'Left border' : 'Accent'}
                </span>
                <button
                  className="btn sm"
                  title="Follow the page's own colour order"
                  onClick={() => setField(i, 'color', undefined)}
                  style={{ opacity: block.color ? 0.6 : 1, fontWeight: block.color ? 400 : 700 }}
                >
                  Auto
                </button>
                {BLOCK_COLORS.map((c) => {
                  // Nothing looks selected until the author actually picks a
                  // colour, so "Auto" stays visibly the current state.
                  const active = block.color === c.id;
                  return (
                    <button
                      key={c.id}
                      title={c.label}
                      aria-label={c.label}
                      aria-pressed={active}
                      onClick={() => setField(i, 'color', c.id)}
                      style={{
                        width: 22,
                        height: 22,
                        borderRadius: 6,
                        background: c.hex,
                        cursor: 'pointer',
                        // The ring, not the swatch, marks the selection —
                        // recolouring it would hide the colour being picked.
                        border: active ? '2px solid var(--text)' : '2px solid transparent',
                        outline: active ? '1px solid var(--border)' : 'none',
                      }}
                    />
                  );
                })}
              </div>
            </div>

            <div className="grid" style={{ gap: 12 }}>
              {type?.fields.map((f) => (
                <FieldEditor
                  key={f.key}
                  field={f}
                  value={block[f.key]}
                  onChange={(v) =>
                    onChange(blocks.map((b, k) => (k === i ? { ...b, [f.key]: v } : b)))
                  }
                  onError={onError}
                  bare
                />
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
}

/**
 * Version history.
 *
 * Every save keeps the previous version, so an edit that wipes something can be
 * undone. Restoring is itself recorded, so you can undo the undo.
 */
function PageHistory({
  page,
  onRestored,
  onError,
}: {
  page: SitePage;
  onRestored: (msg: string) => void;
  onError: (m: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const [revisions, setRevisions] = useState<
    Array<{ id: string; title: string; savedBy: string | null; createdAt: string; blockCount: number | null }>
  >([]);

  const load = useCallback(() => {
    api.listPageRevisions(page.id).then(setRevisions).catch(() => setRevisions([]));
  }, [page.id]);

  useEffect(() => {
    if (open) load();
  }, [open, load]);

  return (
    <div className="panel">
      <div className="row" style={{ justifyContent: 'space-between' }}>
        <strong>Version history</strong>
        <button className="btn sm" onClick={() => setOpen((o) => !o)}>
          {open ? 'Hide' : `Show${revisions.length ? ` (${revisions.length})` : ''}`}
        </button>
      </div>
      {open && (
        <div className="grid" style={{ gap: 8, marginTop: 12 }}>
          {revisions.map((r) => (
            <div key={r.id} className="chip" style={{ justifyContent: 'space-between' }}>
              <span>
                {fmtTime(r.createdAt)}
                <span className="muted" style={{ fontSize: 11, display: 'block' }}>
                  {r.title}
                  {r.blockCount !== null && ` · ${r.blockCount} blocks`}
                  {r.savedBy && ` · ${r.savedBy}`}
                </span>
              </span>
              <button
                className="btn sm"
                onClick={async () => {
                  if (!window.confirm('Restore this version? The current one is kept in history.')) return;
                  try {
                    await api.restorePageRevision(page.id, r.id);
                    onRestored('Version restored.');
                    load();
                  } catch (e) {
                    onError((e as Error).message);
                  }
                }}
              >
                Restore
              </button>
            </div>
          ))}
          {revisions.length === 0 && (
            <span className="muted" style={{ fontSize: 13 }}>
              No earlier versions yet — the next save will create one.
            </span>
          )}
        </div>
      )}
    </div>
  );
}

function FieldEditor({
  field,
  value,
  onChange,
  onError,
  bare = false,
}: {
  field: TemplateField;
  value: unknown;
  onChange: (v: unknown) => void;
  onError: (m: string) => void;
  /** Inside a block the surrounding panel is already there. */
  bare?: boolean;
}) {
  if (field.type === 'items') {
    const items = Array.isArray(value) ? (value as Record<string, string>[]) : [];
    const update = (i: number, key: string, v: string) =>
      onChange(items.map((it, k) => (k === i ? { ...it, [key]: v } : it)));

    return (
      <div className={bare ? '' : 'panel'}>
        <div className="row" style={{ justifyContent: 'space-between', marginBottom: 10 }}>
          <strong>{field.label}</strong>
          <button className="btn sm" onClick={() => onChange([...items, {}])}>
            + Add
          </button>
        </div>
        <div className="grid" style={{ gap: 12 }}>
          {items.map((it, i) => (
            <div key={i} className="stat-tile" style={{ padding: 14 }}>
              <div className="row" style={{ justifyContent: 'space-between', marginBottom: 8 }}>
                <span className="muted" style={{ fontSize: 12 }}>
                  #{i + 1}
                </span>
                <button
                  className="btn sm"
                  onClick={() => onChange(items.filter((_, k) => k !== i))}
                >
                  ✕
                </button>
              </div>
              {field.itemFields?.map((sf) => (
                <div key={sf.key} style={{ marginBottom: 8 }}>
                  <label>{sf.label}</label>
                  {sf.type === 'image' ? (
                    <ImageField
                      value={it[sf.key] ?? ''}
                      onChange={(v) => update(i, sf.key, v)}
                      onError={onError}
                    />
                  ) : sf.type === 'textarea' ? (
                    <textarea
                      rows={3}
                      value={it[sf.key] ?? ''}
                      onChange={(e) => update(i, sf.key, e.target.value)}
                      style={textareaStyle}
                    />
                  ) : (
                    <input
                      value={it[sf.key] ?? ''}
                      onChange={(e) => update(i, sf.key, e.target.value)}
                      style={{ width: '100%' }}
                    />
                  )}
                </div>
              ))}
            </div>
          ))}
          {items.length === 0 && <span className="muted">Nothing yet — use “+ Add”.</span>}
        </div>
      </div>
    );
  }

  if (field.type === 'toggle') {
    // Absent counts as on, so a page written before the option existed keeps
    // showing the section rather than silently losing it.
    const on = value !== false;
    return (
      <div className={bare ? '' : 'panel'}>
        <label className="row" style={{ gap: 8, cursor: 'pointer', marginBottom: 0 }}>
          <input type="checkbox" checked={on} onChange={(e) => onChange(e.target.checked)} />
          {field.label}
        </label>
        {field.help && (
          <p className="muted" style={{ fontSize: 12, margin: '6px 0 0' }}>
            {field.help}
          </p>
        )}
      </div>
    );
  }

  return (
    <div className={bare ? '' : 'panel'}>
      <label>{field.label}</label>
      {field.type === 'image' ? (
        <ImageField value={String(value ?? '')} onChange={onChange} onError={onError} />
      ) : field.type === 'text' ? (
        <input
          value={String(value ?? '')}
          onChange={(e) => onChange(e.target.value)}
          style={{ width: '100%' }}
        />
      ) : field.type === 'html' ? (
        <RichText value={String(value ?? '')} onChange={onChange} />
      ) : (
        <textarea
          rows={3}
          value={String(value ?? '')}
          onChange={(e) => onChange(e.target.value)}
          style={textareaStyle}
        />
      )}
      {field.help && (
        <p className="muted" style={{ fontSize: 12, marginBottom: 0 }}>
          {field.help}
        </p>
      )}
    </div>
  );
}

function ImageField({
  value,
  onChange,
  onError,
}: {
  value: string;
  onChange: (v: string) => void;
  onError: (m: string) => void;
}) {
  const ref = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);

  const upload = async (file: File) => {
    setBusy(true);
    try {
      const { url } = await api.uploadSiteMedia(file);
      onChange(url);
    } catch (e) {
      onError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div>
      <div className="row">
        <button className="btn sm" onClick={() => ref.current?.click()} disabled={busy}>
          {busy ? 'Uploading…' : value ? 'Replace image' : 'Upload image'}
        </button>
        {value && (
          <button className="btn sm" onClick={() => onChange('')}>
            Remove
          </button>
        )}
        <input
          ref={ref}
          type="file"
          accept="image/*"
          hidden
          onChange={(e) => {
            const f = e.target.files?.[0];
            if (f) void upload(f);
            e.target.value = '';
          }}
        />
      </div>
      {value && (
        <img
          src={value.startsWith('/site-media/') ? `/api/site/media/${value.split('/').pop()}` : value}
          alt=""
          style={{ marginTop: 10, maxWidth: 220, borderRadius: 8, display: 'block' }}
        />
      )}
    </div>
  );
}

/**
 * Small rich-text field: format with buttons instead of writing HTML.
 *
 * Uncontrolled on purpose — re-rendering a contenteditable from state on every
 * keystroke moves the caret to the start, which makes typing impossible.
 */
function RichText({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (ref.current && ref.current.innerHTML !== value) ref.current.innerHTML = value;
    // Enter should start a new paragraph, not a bare <div>: browsers default to
    // <div>, which isn't part of the rendered vocabulary and used to be stripped
    // on save — making the return key look broken.
    try {
      document.execCommand('defaultParagraphSeparator', false, 'p');
    } catch {
      /* older browsers: the sanitiser converts <div> to <p> anyway */
    }
    // Only on mount / when switching to another page.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const cmd = (command: string, arg?: string) => {
    document.execCommand(command, false, arg);
    if (ref.current) onChange(ref.current.innerHTML);
  };

  const BUTTONS: Array<[string, string, string?]> = [
    ['Bold', 'bold'],
    ['Italic', 'italic'],
    ['H2', 'formatBlock', 'h2'],
    ['H3', 'formatBlock', 'h3'],
    ['Paragraph', 'formatBlock', 'p'],
    ['• List', 'insertUnorderedList'],
    ['1. List', 'insertOrderedList'],
  ];

  return (
    <div>
      <div className="row" style={{ gap: 6, marginBottom: 8, flexWrap: 'wrap' }}>
        {BUTTONS.map(([label, command, arg]) => (
          <button
            key={label}
            className="btn sm"
            onMouseDown={(e) => e.preventDefault()} // keep the selection
            onClick={() => cmd(command, arg)}
          >
            {label}
          </button>
        ))}
        <button
          className="btn sm"
          onMouseDown={(e) => e.preventDefault()}
          onClick={() => {
            const url = window.prompt('Link address (https://… or /page)');
            if (url) cmd('createLink', url);
          }}
        >
          Link
        </button>
        <button
          className="btn sm"
          title="Insert a line break within the same paragraph"
          onMouseDown={(e) => e.preventDefault()}
          onClick={() => cmd('insertLineBreak')}
        >
          ↵ Break
        </button>
      </div>
      <div
        ref={ref}
        contentEditable
        suppressContentEditableWarning
        onInput={() => ref.current && onChange(ref.current.innerHTML)}
        onBlur={() => ref.current && onChange(ref.current.innerHTML)}
        style={{ ...textareaStyle, minHeight: 200, textAlign: 'left' }}
      />
      <p className="muted" style={{ fontSize: 12, margin: '6px 0 0' }}>
        <strong>Enter</strong> starts a new paragraph · <strong>Shift+Enter</strong> breaks the line
        within one.
      </p>
    </div>
  );
}

const textareaStyle: React.CSSProperties = {
  width: '100%',
  background: 'var(--bg)',
  color: 'var(--text)',
  border: '1px solid var(--border)',
  borderRadius: 8,
  padding: '10px 12px',
  fontSize: 15,
  fontFamily: 'inherit',
};
